import { isEmeisEnabled } from "./emeisFlag";
import {
  isEmeisClauseEnabled,
  isEmeisReviewEnabled,
  isEmeisSignatureEnabled,
  isEmeisSubscriptionEnabled,
} from "./emeisModuleFlags";

/**
 * Notifications about parts of the product that are hidden behind an
 * internal switch. The backend still sends them; the UI leaves them out —
 * from the bell, the panel, the Notifications page, the unread badge and the
 * live toast — until that part's switch is on:
 *  - the Clause Library: "isEmeisClause" (utils/emeisModuleFlags),
 *  - section-level review inside a contract: "isEmeisReview",
 *  - Pactline Native Signature: "isEmeisSignature",
 *  - licensing (subscription, usage, upgrade requests): "isEmeisSubscription",
 *  - the admin tools (providers, reindex): "isEmeis" (emeisFlag).
 *
 * A notification is matched by its type (Emeis.Notifying NotificationType)
 * or, for one without a type, by where it links to.
 */
interface Gate {
  on: () => boolean;
  types: number[];
  routes: string[];
}

const GATES: Gate[] = [
  {
    on: isEmeisClauseEnabled,
    types: [13], // ClauseUpdate
    // The clause pages ("/clause-library" is the link the backend builds from the module name), the
    // Clause Types / Jurisdictions admin pages, and the removal notices
    // (type 8, shared by every module) for a clause type or jurisdiction: those link to
    // "/{entity type}/?id=…", i.e. "/clause type/…" (URL-encoded) and "/jurisdiction/…".
    routes: [
      "/library/clause-templates",
      "/clause-library",
      "/admin/clause-type",
      "/admin/jurisdiction",
      "/clause%20type",
      "/clause type",
      "/jurisdiction",
    ],
  },
  {
    on: isEmeisReviewEnabled,
    types: [14], // SectionReview
    // The in-app links the backend builds: "/contracts/review/<assignment>?id=<contract>" and
    // "/contracts/comment/<thread>?id=<contract>".
    routes: ["/contracts/review", "/contracts/comment"],
  },
  {
    on: isEmeisSignatureEnabled,
    types: [15], // NativeSigning
    // The signing ceremony: "/contracts/sign/<request>?id=<contract>".
    routes: ["/contracts/sign"],
  },
  {
    on: isEmeisSubscriptionEnabled,
    types: [
      9, // UsageAlert — every licensing notice: usage, plan/status, trial, renewal, recharge
      10, // SubscriptionUpgradeRequested
    ],
    routes: ["/admin/licensing", "/admin/subscription"],
  },
  {
    on: isEmeisEnabled,
    types: [],
    routes: ["/admin/providers", "/admin/reindex"],
  },
];

function linkPath(url?: string | null): string {
  if (!url) return "";
  try {
    return new URL(url, window.location.origin).pathname.toLowerCase();
  } catch {
    return url.toLowerCase();
  }
}

/** Whether any notification is currently hidden by a switch. */
export function hidesSomeNotifications(): boolean {
  return GATES.some((g) => !g.on());
}

export function isNotificationVisible(n: {
  notificationType?: number | null;
  deepLinkUrl?: string | null;
}): boolean {
  const type = n.notificationType != null ? Number(n.notificationType) : null;
  const path = linkPath(n.deepLinkUrl);
  return GATES.every(
    (g) =>
      g.on() ||
      !(
        (type != null && g.types.includes(type)) ||
        g.routes.some((r) => path === r || path.startsWith(`${r}/`))
      ),
  );
}

/** The most notifications read in one go to count or list only the visible ones. */
export const VISIBLE_SCAN_SIZE = 1000;
